import React from "react";
import { Box, Paper, Typography, Chip, Button, Tooltip } from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { formatDistanceToNow } from "date-fns";
import { catalogTokens, colorForTag, formatPriceRange, parseServerTimestamp } from "./VendorCatalogTheme";

const VendorCatalogCard = ({ group, onOpenVendorCatalogs }) => {
  const uploaded = parseServerTimestamp(group.latestUploadedAt);
  const MAX_VISIBLE_COLLECTIONS = 3;
  const visibleCollections = group.collections.slice(0, MAX_VISIBLE_COLLECTIONS);
  const hiddenCollectionCount = group.collections.length - visibleCollections.length;

  return (
    <Paper
      elevation={0}
      sx={{
        boxSizing: "border-box",
        p: 2.5,
        height: "100%",
        borderRadius: `${catalogTokens.radius.lg}px`,
        border: `1px solid ${catalogTokens.color.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        transition: "box-shadow 0.18s ease, transform 0.18s ease",
        "&:hover": {
          boxShadow: catalogTokens.shadow.md,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: `${catalogTokens.radius.md}px`,
              bgcolor: catalogTokens.color.primarySoft,
              color: catalogTokens.color.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StorefrontIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700} noWrap title={group.vendorName}>
            {group.vendorName}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${group.catalogCount} ${group.catalogCount === 1 ? "catalog" : "catalogs"}`}
          sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textSecondary, fontWeight: 700, flexShrink: 0 }}
        />
      </Box>

      {group.brands.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {group.brands.map((b) => {
            const color = colorForTag(b.name);
            return (
              <Chip
                key={b.id ?? b.name}
                size="small"
                label={b.name}
                sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 700 }}
              />
            );
          })}
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 32 }}>
        {group.categories.map((cat) => {
          const color = colorForTag(cat.name);
          return (
            <Chip
              key={cat.id}
              size="small"
              label={cat.name}
              sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }}
            />
          );
        })}
        {group.subCategories.map((sc) => (
          <Chip
            key={sc.id ?? `${sc.categoryName}::${sc.name}`}
            size="small"
            variant="outlined"
            label={sc.name}
            sx={{ borderColor: catalogTokens.color.border, color: catalogTokens.color.textSecondary, fontWeight: 600 }}
          />
        ))}
      </Box>

      {group.collections.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {visibleCollections.map((c) => {
            const color = colorForTag(c.name);
            return (
              <Chip
                key={c.id}
                size="small"
                label={c.name}
                title={c.group === "OCCASION" ? "Occasion" : "Celebration"}
                sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }}
              />
            );
          })}
          {hiddenCollectionCount > 0 && (
            <Chip
              size="small"
              label={`+${hiddenCollectionCount}`}
              title={group.collections.slice(MAX_VISIBLE_COLLECTIONS).map((c) => c.name).join(", ")}
              sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textSecondary, fontWeight: 700 }}
            />
          )}
        </Box>
      )}

        <Typography variant="h6" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary }}>
          {formatPriceRange(group.minPrice, group.maxPrice)}
        </Typography>

      <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary }} noWrap title={group.latestUploadedBy}>
        {uploaded ? `Last uploaded ${formatDistanceToNow(uploaded, { addSuffix: true })}` : "Upload date unknown"}
        {group.latestUploadedBy ? ` by ${group.latestUploadedBy}` : ""}
      </Typography>

      <Button
        variant="contained"
        disableElevation
        size="small"
        fullWidth
        startIcon={<ViewListOutlinedIcon fontSize="small" />}
        onClick={() => onOpenVendorCatalogs(group)}
        sx={{
          mt: "auto",
          borderRadius: `${catalogTokens.radius.md}px`,
          textTransform: "none",
          fontWeight: 700,
          bgcolor: catalogTokens.color.primary,
          "&:hover": { bgcolor: catalogTokens.color.primaryDark },
        }}
      >
        {group.catalogCount === 1 ? "View Catalog" : `View ${group.catalogCount} Catalogs`}
      </Button>
    </Paper>
  );
};

export default VendorCatalogCard;
